// src/components/admin/blog/TagsManagement.js
import {
  ArrowLeft,
  Edit,
  Plus,
  RefreshCw,
  Search,
  Tag,
  Trash2
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utils/axiosHandler';
import NotificationBar from '../shared/NotificationBar';

const TagsManagement = ({ showNotification }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [notification, setNotification] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: ''
  });

  const navigate = useNavigate();

  // Local notification function
  const showLocalNotification = (type, message, duration = 5000) => {
    setNotification({ type, message });
    if (duration) {
      setTimeout(() => setNotification(null), duration);
    }
  };

  // Use the passed showNotification if available, otherwise use local one
  const notify = showNotification || showLocalNotification;

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    // Filter tags based on search term
    if (searchTerm.trim() === '') {
      setFilteredTags(tags);
    } else {
      const filtered = tags.filter(
        tag => tag.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTags(filtered);
    }
  }, [tags, searchTerm]);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/blog/tags');

      if (res.status !== 200) throw new Error('Failed to fetch tags');

      const data = await res.data;
      setTags(data);
      setFilteredTags(data);
    } catch (error) {
      console.error('Error fetching tags:', error);
      notify('error', 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const method = isEditing ? 'put' : 'post';
      const url = isEditing
        ? `/api/blog/tags/${formData._id}`
        : '/api/blog/tags';

      await axios({
        method,
        url,
        data: {
          name: formData.name
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Close modal and reset form
      setIsModalOpen(false);
      setFormData({
        name: ''
      });
      setIsEditing(false);

      // Refresh tags
      fetchTags();

      notify('success', `Tag ${isEditing ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} tag:`, error);

      // Axios errors can include a response with details
      const errorMessage = error.response?.data?.error
        || error.message
        || `Failed to ${isEditing ? 'update' : 'create'} tag`;

      notify('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tag) => {
    setFormData({
      _id: tag._id,
      name: tag.name
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (tagId) => {
    if (!window.confirm('Are you sure you want to delete this tag? It will be removed from all posts that use it.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`/api/blog/tags/${tagId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.status !== 200) {
        const errorData = await res.data;
        throw new Error(errorData.error || 'Failed to delete tag');
      }

      // Refresh tags
      fetchTags();

      notify('success', 'Tag deleted successfully');
    } catch (error) {
      console.error('Error deleting tag:', error);
      notify('error', error.message || 'Failed to delete tag');
    } finally {
      setLoading(false);
    }
  };

  // Generate a background color based on the tag name for visual variety
  const getTagColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-yellow-100 text-yellow-800',
      'bg-indigo-100 text-indigo-800',
      'bg-purple-100 text-purple-800',
      'bg-pink-100 text-pink-800',
      'bg-red-100 text-red-800',
      'bg-gray-100 text-gray-800',
    ];

    // Use a simple hash of the name to pick a consistent color
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="min-h-screen bg-white rounded-lg shadow-md">
      {notification && (
        <NotificationBar
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold">
          <button
            onClick={() => navigate('/pitech/blog')}
            style={{
              boxShadow: 'none',
              border: 'none',
              background: 'transparent',
              transform: 'none',
            }}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <span>Tag Management</span>
        </h2>
        <button
          onClick={() => {
            setFormData({
              name: ''
            });
            setIsEditing(false);
            setIsModalOpen(true);
          }}
          className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Tag
        </button>
      </div>

      {/* Search & Filter */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tags..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            onClick={fetchTags}
            className="ml-3 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tags Grid */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
            <p className="mt-2 text-gray-500">Loading tags...</p>
          </div>
        ) : filteredTags.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No tags found</h3>
            <p className="mt-1 text-gray-500">
              {searchTerm ? 'No tags match your search' : 'Get started by creating a new tag'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  setFormData({
                    name: ''
                  });
                  setIsEditing(false);
                  setIsModalOpen(true);
                }}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Tag
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTags.map(tag => (
              <div
                key={tag._id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag.name)}`}>
                    {tag.name}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(tag)}
                      className="text-amber-600 hover:text-amber-800"
                      title="Edit"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(tag._id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center mt-2">
                  <button
                    onClick={() => navigate(`/pitech/blog/posts?tag=${tag.slug}`)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View Posts
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tag Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsModalOpen(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        {isEditing ? 'Edit Tag' : 'Create New Tag'}
                      </h3>

                      <div className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="Tag name"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      isEditing ? 'Update' : 'Create'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <style jsx="true">{`
        button {
          box-shadow: none !important;
          border: none !important;
          transform: none !important;
        }
      `}</style>
    </div>
  );
};

export default TagsManagement;





// // src/components/admin/blog/TagsManagement.js
// import {
//   ArrowLeft,
//   Edit,
//   Plus,
//   RefreshCw,
//   Search,
//   Tag,
//   Trash2
// } from 'lucide-react';
// import React, { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from '../../../utils/axiosHandler';

// const TagsManagement = ({ showNotification }) => {
//   const [tags, setTags] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [filteredTags, setFilteredTags] = useState([]);

//   // Form state
//   const [formData, setFormData] = useState({
//     name: ''
//   });

//   const navigate = useNavigate();

//   useEffect(() => {
//     fetchTags();
//   }, []);

//   useEffect(() => {
//     // Filter tags based on search term
//     if (searchTerm.trim() === '') {
//       setFilteredTags(tags);
//     } else {
//       const filtered = tags.filter(
//         tag => tag.name.toLowerCase().includes(searchTerm.toLowerCase())
//       );
//       setFilteredTags(filtered);
//     }
//   }, [tags, searchTerm]);

//   const fetchTags = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get('/api/blog/tags');

//       if (res.status !== 200) throw new Error('Failed to fetch tags');

//       const data = await res.data;
//       setTags(data);
//       setFilteredTags(data);
//     } catch (error) {
//       console.error('Error fetching tags:', error);
//       showNotification('error', 'Failed to load tags');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//   };
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     try {
//       const token = localStorage.getItem('token');
//       const method = isEditing ? 'put' : 'post';
//       const url = isEditing
//         ? `/api/blog/tags/${formData._id}`
//         : '/api/blog/tags';

//       await axios({
//         method,
//         url,
//         data: {
//           name: formData.name
//         },
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });

//       // Close modal and reset form
//       setIsModalOpen(false);
//       setFormData({
//         name: ''
//       });
//       setIsEditing(false);

//       // Refresh tags
//       fetchTags();

//       showNotification('success', `Tag ${isEditing ? 'updated' : 'created'} successfully`);
//     } catch (error) {
//       console.error(`Error ${isEditing ? 'updating' : 'creating'} tag:`, error);

//       // Axios errors can include a response with details
//       const errorMessage = error.response?.data?.error
//         || error.message
//         || `Failed to ${isEditing ? 'update' : 'create'} tag`;

//       showNotification('error', errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEdit = (tag) => {
//     setFormData({
//       _id: tag._id,
//       name: tag.name
//     });
//     setIsEditing(true);
//     setIsModalOpen(true);
//   };

//   const handleDelete = async (tagId) => {
//     if (!window.confirm('Are you sure you want to delete this tag? It will be removed from all posts that use it.')) {
//       return;
//     }

//     setLoading(true);
//     try {
//       const token = localStorage.getItem('token');
//       const res = await axios.delete(`/api/blog/tags/${tagId}`, {
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });

//       if (res.status !== 200) {
//         const errorData = await res.data;
//         throw new Error(errorData.error || 'Failed to delete tag');
//       }

//       // Refresh tags
//       fetchTags();

//       showNotification('success', 'Tag deleted successfully');
//     } catch (error) {
//       console.error('Error deleting tag:', error);
//       showNotification('error', error.message || 'Failed to delete tag');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Generate a background color based on the tag name for visual variety
//   const getTagColor = (name) => {
//     const colors = [
//       'bg-blue-100 text-blue-800',
//       'bg-green-100 text-green-800',
//       'bg-yellow-100 text-yellow-800',
//       'bg-indigo-100 text-indigo-800',
//       'bg-purple-100 text-purple-800',
//       'bg-pink-100 text-pink-800',
//       'bg-red-100 text-red-800',
//       'bg-gray-100 text-gray-800',
//     ];

//     // Use a simple hash of the name to pick a consistent color
//     const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
//     return colors[hash % colors.length];
//   };

//   return (
//     <div className="min-h-screen bg-white rounded-lg shadow-md">
//       <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
//         <h2 className="text-xl font-semibold">
//           <button
//             onClick={() => navigate('/admin/blog')}
//             style={{
//               boxShadow: 'none',
//               border: 'none',
//               background: 'transparent',
//               transform: 'none',
//             }}
//             className="text-gray-600 hover:text-gray-900"
//           >
//             <ArrowLeft size={24} />
//           </button>
//           <span>Tag Management</span>
//         </h2>
//         <button
//           onClick={() => {
//             setFormData({
//               name: ''
//             });
//             setIsEditing(false);
//             setIsModalOpen(true);
//           }}
//           className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 flex items-center"
//         >
//           <Plus className="h-4 w-4 mr-2" />
//           New Tag
//         </button>
//       </div>

//       {/* Search & Filter */}
//       <div className="p-4 border-b border-gray-200 bg-gray-50">
//         <div className="flex items-center">
//           <div className="relative flex-1">
//             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//               <Search className="h-4 w-4 text-gray-400" />
//             </div>
//             <input
//               type="text"
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               placeholder="Search tags..."
//               className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
//             />
//           </div>

//           <button
//             onClick={fetchTags}
//             className="ml-3 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
//           >
//             <RefreshCw className="h-4 w-4 mr-2" />
//             Refresh
//           </button>
//         </div>
//       </div>

//       {/* Tags List */}
//       <div className="p-6">
//         {loading ? (
//           <div className="text-center py-12">
//             <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
//             <p className="mt-2 text-gray-500">Loading tags...</p>
//           </div>
//         ) : filteredTags.length === 0 ? (
//           <div className="text-center py-12 bg-gray-50 rounded-lg">
//             <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
//             <h3 className="text-lg font-medium text-gray-900">No tags found</h3>
//             <p className="mt-1 text-gray-500">
//               {searchTerm ? 'No tags match your search' : 'Get started by creating a new tag'}
//             </p>
//             {!searchTerm && (
//               <button
//                 onClick={() => {
//                   setFormData({
//                     name: ''
//                   });
//                   setIsEditing(false);
//                   setIsModalOpen(true);
//                 }}
//                 className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700"
//               >
//                 <Plus className="h-4 w-4 mr-2" />
//                 New Tag
//               </button>
//             )}
//           </div>
//         ) : (
//           <div className="flex flex-wrap gap-3">
//             {filteredTags.map(tag => (
//               <div
//                 key={tag._id}
//                 className="relative border rounded-lg p-4 bg-white hover:shadow-md transition-shadow max-w-sm flex-grow"
//               >
//                 <div className="flex justify-between items-start">
//                   <div>
//                     <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-2 ${getTagColor(tag.name)}`}>
//                       {tag.name}
//                     </span>
//                     <p className="text-sm text-gray-500">
//                       Slug: <code className="bg-gray-100 px-1 py-0.5 rounded">{tag.slug}</code>
//                     </p>
//                   </div>
//                   <div className="flex space-x-2">
//                     <button
//                       onClick={() => handleEdit(tag)}
//                       className="text-amber-600 hover:text-amber-800"
//                       title="Edit"
//                     >
//                       <Edit className="h-5 w-5" />
//                     </button>
//                     <button
//                       onClick={() => handleDelete(tag._id)}
//                       className="text-red-600 hover:text-red-800"
//                       title="Delete"
//                     >
//                       <Trash2 className="h-5 w-5" />
//                     </button>
//                   </div>
//                 </div>

//                 <div className="mt-3 pt-3 border-t border-gray-100">
//                   <button
//                     onClick={() => navigate(`/admin/blog/posts?tag=${tag.slug}`)}
//                     className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
//                   >
//                     View Posts with this Tag
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>

//       {/* Tag Form Modal */}
//       {isModalOpen && (
//         <div className="fixed inset-0 z-10 overflow-y-auto">
//           <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
//             <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsModalOpen(false)}></div>

//             <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
//               <form onSubmit={handleSubmit}>
//                 <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
//                   <div className="sm:flex sm:items-start">
//                     <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
//                       <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
//                         {isEditing ? 'Edit Tag' : 'Create New Tag'}
//                       </h3>

//                       <div>
//                         <label htmlFor="name" className="block text-sm font-medium text-gray-700">
//                           Name <span className="text-red-500">*</span>
//                         </label>
//                         <input
//                           type="text"
//                           id="name"
//                           name="name"
//                           required
//                           value={formData.name}
//                           onChange={handleInputChange}
//                           className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-amber-500 focus:border-amber-500"
//                           placeholder="Tag name"
//                         />
//                         <p className="mt-2 text-sm text-gray-500">
//                           A tag name should be concise and descriptive.
//                         </p>
//                       </div>

//                       {isEditing && (
//                         <div className="mt-3 p-3 bg-gray-50 rounded-md">
//                           <p className="text-sm text-gray-600">
//                             Slug: <code className="bg-white px-1 py-0.5 rounded border">{formData.slug}</code>
//                           </p>
//                           <p className="text-xs text-gray-500 mt-1">
//                             The slug will be updated automatically based on the name.
//                           </p>
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </div>

//                 <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
//                   <button
//                     type="submit"
//                     disabled={loading}
//                     className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
//                   >
//                     {loading ? (
//                       <>
//                         <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
//                         Saving...
//                       </>
//                     ) : (
//                       isEditing ? 'Update' : 'Create'
//                     )}
//                   </button>
//                   <button
//                     type="button"
//                     onClick={() => setIsModalOpen(false)}
//                     className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
//                   >
//                     Cancel
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//       )}
//       <style jsx="true">{`
//         button {
//           box-shadow: none !important;
//           border: none !important;
//           transform: none !important;
//         }
        
//        `}</style>
//     </div>
//   );
// };

// export default TagsManagement;